import React, { useState, useMemo } from 'react';
import FlexComponent from '../FlexComponent';
import { Button } from 'primereact/button';
import AppModal from '../../shared/AppModal';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';

// Optional: if you're using axios (recommended)
import axios from '../../utils/axios';
import { appSubmission, getCustomFields, getPipelines, getUserActivityLogs, registeredUserList, UpdateLeadStage } from '../../utils/urls';
import AppPopup from './appsKanban/AppPopup';
import { useSelector } from 'react-redux';
import { Avatar } from 'primereact/avatar';
import UserActivityDashboardKPIs from '../../pages/app/UserActivityDashboardKPIs';

function UserActivityLog({ data, toast, start, end }) {
    const [isLogsOpen, setIsLogsOpen] = useState(false);
    const [activityLogs, setActivityLogs] = useState([]);
    const [dashboardKpis, setDashboardKpis] = useState(null);
    const [logsMeta, setLogsMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState(null);
    const [localDateRange, setLocalDateRange] = useState(null);

    const userId = useSelector((state) => state.user.userId);
    const isSuperuser = useSelector((state) => state.user.isSuperuser);

    const [pipelines, setPipelines] = useState([]);
    const [pipelineId, setPipelineId] = useState("");
    const [pipelineStages, setPipelineStages] = useState();
    const [stageIds, setStageIds] = useState("")
    const [allPipelinesData, setAllPipelinesData] = useState([])
    const [inactivePipelineId, setInactivePipelineId] = useState("")

    const [isOpen, setIsOpen] = useState(false);
    const [applicantData, setApplicantData] = useState();
    const [popupLoad, setPopupLoad] = useState(false);
    const [registeredUsersList, setRegisteredUsersList] = useState([]);
    const [loanDetailsCustomFields, setLoanDetailsCustomFields] = useState([])
    const [realEstateTransactionsCustomFields, setRealEstateTransactionsCustomFields] = useState([])
    const [allCustomFields, setAllCustomFields] = useState([])
    const loanDetailsFields = ["Onedrive Link", "Gdrive Link", "Loan File ID", "Purchase Price", "Loan Amount", "Est. Taxes", "Loan Amount Status", "Closing Date", "Subject Property Address", "Sellers Concession", "First Payment Date", "Insurance Inspection Date"]
    const realEstateTransactionFields = ["Real Estate Gdrive Link"]
    // const [checklistsData, setChecklistsData] = useState([])
    const [visibleCount, setVisibleCount] = useState(100);

    const formatDate = (date) => {
        if (!date) return '';
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const fetchActivityLogs = async (userId, startOverride, endOverride) => {
        if (!userId) return;

        setLoading(true);
        setError(null);

        const s = startOverride ?? start;
        const e = endOverride ?? end;

        try {
            const response = await axios.get(`${getUserActivityLogs}${userId}/?startTime=${s}&endTime=${e}`);
            setActivityLogs(response.data?.activities || response.data || []);
            setDashboardKpis(response.data?.dashboard_kpis);
            setLogsMeta({
                first_activity: response.data?.first_activity,
                last_activity: response.data?.last_activity,
                time_active: response.data?.time_active,
            });
        } catch (err) {
            console.error('Failed to load activity logs:', err);
            setError('Could not load activity logs. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleViewClick = () => {
        if (!data?.user_id) {
            console.warn('No user_id provided');
            return;
        }

        setVisibleCount(100);
        setSelectedFilter(null);
        setLocalDateRange(start && end ? [new Date(start), new Date(end)] : null);
        setIsLogsOpen(true);

        fetchActivityLogs(data.user_id);
    };


    const handleLogsClose = () => {
        setIsLogsOpen(false);
        // Optional: clear data if you want to force refetch next time
        // setActivityLogs([]);
        // setError(null);
    };

    function convertDate(isoString) {
        if (!isoString) return "";
        const [datePart, timePart] = isoString.split(/T| /);
        const [year, monthNum, day] = datePart?.split('-') ?? [];
        const [hour, minute] = timePart?.split(':') ?? [];
        if (!year || !hour) return isoString;
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const month = months[parseInt(monthNum, 10) - 1];
        const h24  = parseInt(hour, 10);
        const ampm = h24 >= 12 ? 'pm' : 'am';
        const h12  = String(h24 % 12 || 12).padStart(2, '0');
        return `${month} ${day}, ${year} | ${h12}:${minute} ${ampm}`;
    }

    const activityCountMap = useMemo(() => {
        if (!activityLogs?.length) return {};

        return activityLogs.reduce((acc, log) => {
            const activity = log.activity;

            if (
                activity &&
                typeof activity === "string" &&
                activity.trim() !== ""
            ) {
                acc[activity] = (acc[activity] || 0) + 1;
            }

            return acc;
        }, {});
    }, [activityLogs]);

    const activityOptions = useMemo(() => {
        return Object.entries(activityCountMap).map(([activity, count]) => ({
            label: `${activity} (${count})`,
            value: activity
        }));
    }, [activityCountMap]);


    const displayedLogs = useMemo(() => {
        if (!selectedFilter) return activityLogs;

        return activityLogs.filter(log => log.activity === selectedFilter);
    }, [activityLogs, selectedFilter]);

    const visibleLogs = useMemo(() => {
        return displayedLogs.slice(0, visibleCount);
    }, [displayedLogs, visibleCount]);

    const getAllPipelines = () => {
        if (userId) {
            const url = `${getPipelines}?userId=${userId}`;
            axios.get(url, {
                headers: { 'Content-Type': 'application/json' }
            })
                .then((response) => {
                    if (response.status === 200) {
                        setAllPipelinesData(response?.data)
                        const options = response?.data?.filter((pipeline) => {
                            if (pipeline.name === "Inactive Apps") {
                                setInactivePipelineId(pipeline.id)
                                return false;
                            }
                            return true;
                        })?.map((pipeline) => ({
                            label: pipeline.name,
                            value: pipeline.id,
                        })) || [];
                        setPipelines(options);
                        const stages = response.data
                        const item = stages?.find(item => item.id == 1);
                        setPipelineStages(item.stages)
                    }
                })
                .catch((error) => {
                    // Handle error
                });
        }
    };

    const handleStageUpdate = (contact_id, form_id, stage, prevStage, emails, isLoad = false, stageId = "") => {
        // console.log(stage, prevStage)
        const data = {
            // stage: stage,
            userId: userId,
            stage_id: stageId ? stageId : stageIds[stage],
            email_list: emails
        }
        console.log("datttttttttt", data);

        const stageUrl = `${UpdateLeadStage}${contact_id}/${form_id}`
        axios.post(stageUrl, data, {
            headers: { 'Content-Type': 'application/json' },
        })
            .then((response) => {
                if (response.status === 200) {
                    toast.current.show({ severity: 'success', summary: 'Success', detail: 'Successfully Updated', life: 3000 });
                    const url = `${appSubmission}${form_id}?pipelineId=${pipelineId}`
                    axios.get(url, {
                        headers: { 'Content-Type': 'application/json' }
                    })
                        .then((response) => {
                            if (response.status === 200) {
                            }
                        })

                }
                else if (response.status === 208) {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: response.data, life: 3000 });
                }
                else {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 });
                }
            })
            .catch((error) => {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 });
            })
    }

    const handleClose = () => {
        setIsOpen(false);
    };

    const getAllCustomFields = () => {
        const listUrl = `${getCustomFields}`;
        axios.get(listUrl, {
            headers: { 'Content-Type': 'application/json' }
        })
            .then((response) => {
                if (response.status === 200) {
                    // setAllCustomFields(response.data);
                    let loanDetailsData = response.data.filter(data => loanDetailsFields.includes(data.title))
                    let realEstateTransactionsData = response.data.filter(data => realEstateTransactionFields.includes(data.title))
                    let restCustomFieldsData = response.data.filter(data => !loanDetailsFields.includes(data.title) && !realEstateTransactionFields.includes(data.title))
                    setLoanDetailsCustomFields(loanDetailsData)
                    setRealEstateTransactionsCustomFields(realEstateTransactionsData)
                    setAllCustomFields(restCustomFieldsData)
                }
            })
            .catch((error) => {
                // Handle error
            });
    };

    const openPopupFn = (formId, pipelineId = 1) => {
        setPipelineId(pipelineId);
        setIsOpen(true);
        setPopupLoad(true);
        getAllCustomFields();
        getAllPipelines()
        const url = `${appSubmission}${formId}?pipelineId=1`;
        axios.get(url, {
            headers: { 'Content-Type': 'application/json' }
        })
            .then((response) => {
                if (response.status === 200) {
                    const data = response.data;
                    setApplicantData(data);
                    setPopupLoad(false);
                }
            })
            .catch((error) => {
                // Handle error
            });

        const registeredUsersUrl = `${registeredUserList}`;
        axios.get(registeredUsersUrl, {
            headers: { 'Content-Type': 'application/json' }
        })
            .then((response) => {
                if (response.status === 200) {
                    const options = response?.data?.map((user) => ({
                        label: user.name,
                        value: user.user_id,
                        image: user.image
                    })) || [];
                    setRegisteredUsersList(options);
                }
            })
            .catch((error) => {
                // Handle error
            });
    };

    const getInitials = (name) => {
        const words = name.split(' ');
        const firstInitial = words[0] ? words[0][0].toUpperCase() : '';
        const secondInitial = words[1] ? words[1][0].toUpperCase() : '';
        return firstInitial + secondInitial;
    };

    const extractCallLink = (description) => {
        const urlRegex = /(https:\/\/api\.twilio\.com[^\s]+)/;
        const matches = description.match(urlRegex);

        return matches ? matches[0] : null;
    };

    console.log("data---", data);
    


    return (
        <FlexComponent gap="2rem" style={{ zIndex: "1", padding: ".75rem", width: "auto" }}>
            <div
                onClick={handleViewClick}
                className="appItem"
                style={{
                    width: "220px",
                    boxShadow: "-1px -2px 0px #ffffff40",
                    padding: '1rem',
                    cursor: 'pointer',
                    background: 'rgba(255, 255, 255, 0.06)',
                }}
            >
                <FlexComponent flexDirection="column" alignItems="start" gap=".3rem" style={{ marginTop: "1rem" }}>
                    <div
                        title={data?.user_full_name || data?.username}
                        style={{
                            color: "white",
                            maxWidth: "190px",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap"
                        }}
                    >
                        {data?.user_full_name || data?.username}
                    </div>
                    <div
                        title={data?.user_email}
                        style={{
                            color: "white",
                            maxWidth: "190px",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap"
                        }}
                    >
                        {data?.user_email || "N/A"}
                    </div>
                    <div style={{ color: "white" }}>
                        <span style={{ color: "grey" }}>📞 Calls: </span>
                        {data?.call_data || "0"}
                    </div>
                    <div style={{ color: "white" }}>
                        <span style={{ color: "grey" }}>📧 Emails: </span>
                        {data?.email_count || "0"}
                    </div>
                    <div style={{ color: "white" }}>
                        <span style={{ color: "grey" }}>💬 SMS: </span>
                        {data?.sms_count || "0"}
                    </div>
                </FlexComponent>
            </div>

            <AppModal
                visible={isLogsOpen}
                onHide={handleLogsClose}
                header={`Activity Logs - ${data?.user_full_name || data?.username || 'User'}`}
                panelStyle={{ width: '90vw' }}
            >
                <div className='activityLogItem' style={{ marginTop: '1rem', minHeight: "70vh" }}>
                    {error ? (
                        <div style={{ color: "#ff6b6b", padding: "1rem", textAlign: "center" }}>
                            {error}
                            <Button
                                label="Retry"
                                className="p-button-warning"
                                onClick={() => fetchActivityLogs(data.user_id)}
                                style={{ marginTop: "1rem" }}
                            />
                        </div>
                    ) : (
                        <div style={{ color: "white" }}>

                            {/* KPI cards — skeleton while loading */}
                            <FlexComponent>
                                {loading ? (
                                    <FlexComponent justifyContent="center" gap="0.75rem" flexWrap="wrap" style={{ width: "100%" }}>
                                        {Array.from({ length: 8 }).map((_, i) => (
                                            <div key={i} className="loadingAppItem" style={{ width: "80px", height: "58px", margin: 0, borderRadius: "0.5rem" }} />
                                        ))}
                                    </FlexComponent>
                                ) : (
                                    dashboardKpis && <UserActivityDashboardKPIs data={dashboardKpis} userId={data?.user_id} />
                                )}
                            </FlexComponent>

                            {/* First activity / last activity / time active */}
                            <FlexComponent justifyContent="center" gap="1.5rem" flexWrap="wrap" style={{ marginTop: "1rem" }}>
                                {loading ? (
                                    Array.from({ length: 3 }).map((_, i) => (
                                        <div key={i} className="loadingAppItem" style={{ width: "160px", height: "40px", margin: 0, borderRadius: "0.5rem" }} />
                                    ))
                                ) : logsMeta ? (
                                    <>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ fontSize: '11px', color: '#b1b1b1', marginBottom: '0.2rem' }}>First Activity</div>
                                            <div style={{ fontSize: '13px', color: 'white' }}>{convertDate(logsMeta.first_activity) || '—'}</div>
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ fontSize: '11px', color: '#b1b1b1', marginBottom: '0.2rem' }}>Last Activity</div>
                                            <div style={{ fontSize: '13px', color: 'white' }}>{convertDate(logsMeta.last_activity) || '—'}</div>
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ fontSize: '11px', color: '#b1b1b1', marginBottom: '0.2rem' }}>Time Active</div>
                                            <div style={{ fontSize: '13px', color: 'white' }}>{logsMeta.time_active || '—'}</div>
                                        </div>
                                    </>
                                ) : null}
                            </FlexComponent>

                            {/* Activity count badges — skeleton while loading */}
                            {loading ? (
                                <FlexComponent justifyContent="center" gap="0.75rem" flexWrap="wrap" style={{ marginTop: "1rem" }}>
                                    {Array.from({ length: 4 }).map((_, i) => (
                                        <div key={i} className="loadingAppItem" style={{ width: "110px", height: "58px", margin: 0, borderRadius: "0.5rem" }} />
                                    ))}
                                </FlexComponent>
                            ) : Object.keys(activityCountMap).length > 0 ? (
                                <FlexComponent justifyContent="center" gap="0.75rem" flexWrap="wrap" style={{ marginTop: "1rem" }}>
                                    {(() => {
                                        const priority = ['Call', 'Email', 'SMS'];
                                        return Object.entries(activityCountMap).sort(([a], [b]) => {
                                            const ai = priority.indexOf(a);
                                            const bi = priority.indexOf(b);
                                            if (ai !== -1 && bi !== -1) return ai - bi;
                                            if (ai !== -1) return -1;
                                            if (bi !== -1) return 1;
                                            return 0;
                                        });
                                    })().map(([activity, count]) => (
                                        <div
                                            key={activity}
                                            style={{
                                                background: 'rgba(255,255,255,0.06)',
                                                border: '1px solid rgba(255,255,255,0.12)',
                                                borderRadius: '0.5rem',
                                                padding: '0.6rem 1rem',
                                                minWidth: '110px',
                                                textAlign: 'center',
                                            }}
                                        >
                                            <div style={{ fontSize: '11px', color: '#b1b1b1', marginBottom: '0.25rem' }}>{activity}</div>
                                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'white' }}>{count}</div>
                                        </div>
                                    ))}
                                </FlexComponent>
                            ) : null}

                            <FlexComponent justifyContent="center" gap="1rem" alignItems="center" flexWrap="wrap" style={{ marginTop: "1rem" }}>
                                {loading
                                    ? <div className="loadingAppItem" style={{ width: '60px', height: '32px', margin: 0, borderRadius: '0.4rem' }} />
                                    : <div>Total: {activityLogs?.length}</div>
                                }
                                {loading
                                    ? <div className="loadingAppItem" style={{ width: '230px', height: '32px', margin: 0, borderRadius: '0.4rem' }} />
                                    : (
                                        <div style={{ width: "230px" }}>
                                            <Dropdown
                                                value={selectedFilter}
                                                options={activityOptions}
                                                onChange={(e) => setSelectedFilter(e.value)}
                                                placeholder="Filter by Activity"
                                                filterBy="label"
                                                style={{ width: '100%' }}
                                                showClear
                                            />
                                        </div>
                                    )
                                }
                                <Calendar
                                    value={localDateRange}
                                    onChange={(e) => {
                                        setLocalDateRange(e.value);
                                        if (e.value?.[0] && e.value?.[1]) {
                                            fetchActivityLogs(data.user_id, formatDate(e.value[0]), formatDate(e.value[1]));
                                        }
                                    }}
                                    selectionMode="range"
                                    readOnlyInput
                                    showButtonBar
                                    placeholder="Select Date Range"
                                    style={{ width: '230px', padding: "5px" }}
                                />
                            </FlexComponent>
                            <div style={{ marginTop: "1rem" }} >
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <div key={i} className="loadingAppItem" style={{ width: '100%', height: '80px', margin: '0 0 10px 0', borderRadius: '0.5rem' }} />
                                    ))
                                ) : activityLogs.length === 0 ? (
                                    <div style={{ color: "white", textAlign: "center", padding: "2rem" }}>
                                        No activity logs found for this user.
                                    </div>
                                ) : displayedLogs.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '2rem', color: '#aaa' }}>
                                        No activities match the selected filter.
                                    </div>
                                ) : (
                                    visibleLogs.map((log, index) => (
                                        <div
                                            key={log.id || index}
                                            style={{
                                                padding: ".75rem",
                                                borderBottom: "1px solid rgba(255,255,255,0.12)",
                                            }}
                                        >
                                            <div style={{ width: '100%' }}>
                                                <FlexComponent gap="1rem" className="commentSectionContainer" alignItems="start" >

                                                    {/* Avatar */}
                                                    <div>
                                                        <Avatar
                                                            image={data.profile_image_url}
                                                            size="large"
                                                            shape="circle"
                                                            style={{ width: '35px', height: '35px', color: 'black' }}
                                                            label={getInitials(data.user_full_name)}
                                                        />
                                                    </div>

                                                    {/* Comment Content */}
                                                    <div className="commentSectionDiv " style={{ width: '93%' }}>

                                                        {/* Name + Date */}
                                                        <FlexComponent justifyContent="start" style={{ gap: '0.5rem' }} flexWrap="wrap">
                                                            <p style={{ fontSize: '14px', color: 'white', fontWeight: 'bold', margin: 0 }}>
                                                                {data.user_full_name} ↔ {log.client_name}
                                                            </p>
                                                            <p style={{ fontSize: '12px', color: '#b1b1b1', margin: 0 }}>
                                                                {convertDate(log.created_at)}
                                                            </p>
                                                            {log.activity_ref_type === "app" && log.activity_ref_id &&
                                                                <button onClick={() => { openPopupFn(log.activity_ref_id, 1) }} style={{ background: "black", color: "white" }} className="action-btn view">View App</button>
                                                            }
                                                            {log.contact_url && <button onClick={() => window.open(log.contact_url, '_blank')} style={{ background: "black", color: "white" }} className="action-btn view">View Contact</button>}
                                                            
                                                        </FlexComponent>
                                                        {/* Message */}
                                                        {extractCallLink(log.description) ?
                                                            <FlexComponent
                                                                justifyContent="start"
                                                                gap="0.3rem"
                                                                style={{
                                                                    marginTop: ".5rem",
                                                                    background: '#0808086b',
                                                                    borderRadius: ".5rem",
                                                                    padding: "1rem"
                                                                }}
                                                            >
                                                                <audio style={{ width: "100%" }} controls>
                                                                    <source src={extractCallLink(log.description)} type="audio/mpeg" />
                                                                </audio>
                                                            </FlexComponent>
                                                            :
                                                            <FlexComponent style={{
                                                                margin: '0.5rem 0px', border: '2px solid grey',
                                                                padding: '0.5rem',
                                                                background: 'rgba(8, 8, 8, 0.42)',
                                                                borderRadius: '0.5rem'
                                                            }} >
                                                                <div style={{ wordBreak: 'break-word', margin: 0, color: 'white' }}>
                                                                    {log.description}
                                                                </div>
                                                            </FlexComponent>

                                                        }
                                                    </div>
                                                </FlexComponent>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                            {visibleCount < displayedLogs.length && (
                                <FlexComponent justifyContent="center" style={{ marginTop: "1rem" }}>
                                    <Button
                                        label="Load More"
                                        onClick={() => setVisibleCount(prev => prev + 100)}
                                    />
                                </FlexComponent>
                            )}
                        </div>
                    )}
                </div>
                <AppModal visible={isOpen} onHide={handleClose}>
                    <div style={{ marginTop: '1rem', minHeight: "70vh" }}>
                        {!popupLoad ? (
                            <AppPopup
                                isFromTable={true}
                                applicantData={applicantData}
                                pipelineId={pipelineId}
                                id={applicantData?.app_status}
                                pipelineStages={pipelineStages}
                                allPipelinesData={allPipelinesData}
                                isFromSearch={true}
                                allCustomFields={allCustomFields}
                                loanDetailsCustomFields={loanDetailsCustomFields}
                                realEstateTransactionsCustomFields={realEstateTransactionsCustomFields}
                                // setBoardSections={setBoardSections}
                                setApplicantData={setApplicantData}
                                isFromOtherPages={true}
                                handleStageUpdate={handleStageUpdate}
                                usersList={registeredUsersList}
                                userId={userId}
                                handleClose={handleClose}
                                toast={toast}
                            />
                        ) : (
                            <FlexComponent flexDirection="column" justifyContent="center">
                                <div style={{ minWidth: "40vw", height: "100%" }} className='loadingAppItem'></div>
                            </FlexComponent>
                        )}
                    </div>
                </AppModal>
            </AppModal>
        </FlexComponent>
    );
}

export default UserActivityLog;